/**
 * Shared component style utilities for MUI theme overrides
 * These factories generate consistent styles across dark, light, and grayscale themes
 */

import type { Theme } from '@mui/material/styles'

/**
 * Shared breakpoint values used across all themes
 */
export const breakpointValues = {
  xs: 100,
  sm: 700,
  md: 1060,
  lg: 1480,
  xl: 1920,
}

/**
 * Minimal scrollbar styles for theme contexts
 * Uses native/default scrollbar appearance
 */
export const themeScrollbarStyles = (theme: Theme) => {
  return { scrollbarColor: `${theme.palette.text.primary} ${theme.palette.background.default}` }
}
/**
 * Minimal scrollbar styles for dark theme contexts
 * Uses native/default scrollbar appearance
 */
export const darkScrollbarStyles = {
  scrollbarColor: `rgba(255,255,255,1) rgba(49, 57, 70, 1)`,
}

/**
 * Minimal scrollbar styles for light theme contexts
 * Uses native/default scrollbar appearance
 */
export const lightScrollbarStyles = {
  scrollbarColor: `rgba(0,0,0,1) rgba(255,255,255,1)`,
}

/**
 * DataGrid checkbox styles - identical across all themes
 */
export const dataGridCheckboxStyles = {
  '& .MuiCheckbox-root svg': {
    width: 16,
    height: 16,
    backgroundColor: 'transparent',
    border: '1px solid #d9d9d9',
    borderRadius: 2,
  },
  '& .MuiCheckbox-root svg path': {
    display: 'none',
  },
  '& .MuiCheckbox-root.Mui-checked:not(.MuiCheckbox-indeterminate) svg': {
    backgroundColor: '#1890ff',
    borderColor: '#1890ff',
  },
  '& .MuiCheckbox-root.Mui-checked .MuiIconButton-label:after': {
    position: 'absolute',
    display: 'table',
    border: '2px solid #fff',
    borderTop: 0,
    borderLeft: 0,
    transform: 'rotate(45deg) translate(-50%,-50%)',
    opacity: 1,
    transition: 'all .2s cubic-bezier(.12,.4,.29,1.46) .1s',
    content: '""',
    top: '50%',
    left: '39%',
    width: 5.71428571,
    height: 9.14285714,
  },
  '& .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiIconButton-label:after': {
    width: 8,
    height: 8,
    backgroundColor: '#1890ff',
    transform: 'none',
    top: '39%',
    border: 0,
  },
}

/**
 * Hidden search column styles - identical across all themes
 */
export const hiddenSearchColumnStyles = {
  '& .MuiDataGrid-cell[data-field="__search"]': {
    display: 'none !important',
    width: 0,
    minWidth: 0,
    padding: 0,
    border: 0,
  },
  '& .MuiDataGrid-columnHeader[data-field="__search"]': {
    display: 'none !important',
  },
}

/**
 * DataGrid virtual scroller styles for proper flex layout
 */
export const dataGridVirtualScrollerStyles = {
  '& .MuiDataGrid-main': {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  '& .MuiDataGrid-virtualScroller': {
    flexGrow: 1,
    minHeight: 0,
    overflow: 'auto !important',
  },
}

/**
 * Common Paper component styles
 */
export const paperBaseStyles = (theme: Theme) => ({
  backgroundImage: 'none',
  backgroundColor: theme.palette.background.paper,
  borderWidth: 0,
  alignContent: 'center',
  textAlign: 'center' as const,
  display: 'flexbox',
  width: 'auto',
  borderRadius: '5px',
  boxShadow: 'none',
})

/**
 * Common Collapse component styles
 */
export const collapseStyles = {
  root: {
    marginRight: '30px',
    marginLeft: '30px',
  },
}

/**
 * Common Table component styles
 */
export const tableStyles = {
  root: {
    width: '100%',
  },
}

/**
 * Common TableContainer styles
 */
export const tableContainerStyles = {
  root: {
    backgroundColor: 'none',
  },
}

/**
 * Common Menu styles
 */
export const menuStyles = {
  root: {
    width: '20%',
  },
}

/**
 * Common InputLabel outlined styles
 */
export const inputLabelOutlinedStyles = {
  fontSize: '16px',
  transform: 'translate(9px, 11px) scale(1)',
  '&.MuiInputLabel-shrink': {
    transform: 'translate(14px, -10px) scale(0.75)',
  },
}

/**
 * Common Autocomplete base styles
 */
export const autocompleteBaseStyles = {
  input: {
    width: '100%',
  },
  root: {
    width: '100%',
    height: '100%',
  },
  popper: {
    width: '100%',
  },
  paper: {
    textAlign: 'left' as const,
    width: '100%',
  },
}

/**
 * Common Select styles
 */
export const selectBaseStyles = {
  '& .MuiSelect-select': {
    textAlign: 'center',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSvgIcon-root': {
    display: 'none',
  },
}

/**
 * Common FormControl styles
 */
export const formControlStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'left',
    justifyContent: 'center',
  },
}

/**
 * Common FormLabel styles
 */
export const formLabelStyles = {
  root: {
    fontSize: '18px',
  },
}

/**
 * Common InputBase input styles
 */
export const inputBaseInputStyles = {
  fontSize: '14px',
}

/**
 * Common Dialog paper styles
 */
export const dialogPaperStyles = {
  maxHeight: '100vh',
  overflowY: 'auto' as const,
  borderRadius: '10px',
  boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.3)',
}

/**
 * Common Tooltip styles
 */
export const tooltipBaseStyles = {
  fontSize: '0.75rem',
  padding: '10px',
  borderRadius: '4px',
  textAlign: 'center' as const,
}

/**
 * Common Card base styles
 */
export const cardBaseStyles = {
  borderRadius: '4px',
  alignContent: 'center',
  textAlign: 'center' as const,
  display: 'flexbox',
  width: 'auto',
  overflow: 'auto',
}

/**
 * Common Accordion styles
 */
export const accordionBaseStyles = {
  fontSize: '14px',
  marginBottom: '8px',
  width: '100%',
}

/**
 * Common AccordionSummary styles
 */
export const accordionSummaryBaseStyles = {
  gutters: {
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: 'none',
    },
  },
  root: {
    margin: '2px',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'scale(1.01)',
    },
  },
}

/**
 * Common AccordionDetails styles
 */
export const accordionDetailsStyles = {
  root: {
    padding: 0,
  },
}

/**
 * Common Grid styles
 */
export const gridBaseStyles = {
  item: {
    item: {
      paddingTop: '0px !important',
      paddingLeft: '0px !important',
    },
  },
  root: {
    paddingTop: 0,
  },
}

/**
 * Quill editor styles for dark mode
 */
export const quillDarkStyles = {
  height: '100%',
  borderColor: 'rgba(255, 255, 255, 0.12)',
  color: 'white',
  backgroundColor: 'rgb(30 43 59)',
  '& .ql-toolbar': {
    backgroundColor: 'black',
  },
}

/**
 * Quill editor styles for light mode
 */
export const quillLightStyles = {
  height: '150px',
  borderColor: 'rgba(0,0,0,0.22)',
  color: 'black',
}
